import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Wine, RotateCcw } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow, EASE } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { key: "vini", testid: "menu-tab-vini-button" },
  { key: "taglieri", testid: "menu-tab-food-button" },
  { key: "birre", testid: "menu-tab-birre-button" },
];
const SUBS = ["tutti", "rossi", "bianchi", "bollicine", "dolci"];

const fmt = (p) => `\u20AC ${Number(p).toFixed(2).replace(".", ",")}`;

export const Menu = () => {
  const { t, isEn } = useLanguage();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("vini");
  const [sub, setSub] = useState("tutti");
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    setError(false);
    axios
      .get(`${API}/menu`)
      .then((r) => setItems(r.data.items))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (i.group !== tab) return false;
      if (tab === "vini" && sub !== "tutti" && i.category !== sub) return false;
      if (!q) return true;
      const desc = isEn ? i.desc_en : i.desc_it;
      return `${i.name} ${desc || ""}`.toLowerCase().includes(q);
    });
  }, [items, tab, sub, query, isEn]);

  const priceCell = (item) =>
    item.group === "vini" ? (
      <div className="text-right shrink-0">
        <p className="font-mono text-sm text-gold" data-testid={`menu-price-glass-${item.id}`}>
          {fmt(item.price_glass)} <span className="text-cream/50 text-[11px]">/ {t.menu.glass}</span>
        </p>
        <p className="font-mono text-sm text-gold/70 mt-1">
          {fmt(item.price_bottle)} <span className="text-cream/50 text-[11px]">/ {t.menu.bottle}</span>
        </p>
      </div>
    ) : (
      <p className="font-mono text-base text-gold shrink-0" data-testid={`menu-price-${item.id}`}>
        {fmt(item.price)}
      </p>
    );

  return (
    <section id="menu" className="relative py-24 sm:py-32 bg-burgundy overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gold/10 blur-3xl rounded-full" />

      <div className="relative max-w-[1200px] mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="max-w-xl">
            <Reveal>
              <Eyebrow light>{t.menu.chapter}</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08] text-cream">
                {t.menu.title}
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-5 text-base sm:text-lg font-light leading-relaxed text-cream/70">{t.menu.subtitle}</p>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="w-full sm:w-auto">
            <label className="relative block sm:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/50" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.menu.search}
                data-testid="menu-search-input"
                className="w-full rounded-full border border-cream/20 bg-cream/5 backdrop-blur px-11 py-3 text-sm text-cream placeholder:text-cream/40 outline-none focus:border-gold/60 focus:ring-2 focus:ring-gold/20 transition-all"
              />
            </label>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-b border-cream/15">
            {TABS.map(({ key, testid }) => (
              <button
                key={key}
                data-testid={testid}
                onClick={() => {
                  setTab(key);
                  setSub("tutti");
                }}
                className={`relative pb-4 text-sm sm:text-base font-medium tracking-wide transition-colors ${
                  tab === key ? "text-gold" : "text-cream/60 hover:text-cream"
                }`}
              >
                {t.menu.tabs[key]}
                {tab === key && (
                  <motion.span
                    layoutId="menu-tab-underline"
                    className="absolute inset-x-0 -bottom-px h-0.5 bg-gold"
                    transition={{ duration: 0.4, ease: EASE }}
                  />
                )}
              </button>
            ))}
          </div>
        </Reveal>

        {tab === "vini" && (
          <div className="mt-6 flex flex-wrap gap-2">
            {SUBS.map((s) => (
              <button
                key={s}
                data-testid={`menu-sub-${s}-button`}
                onClick={() => setSub(s)}
                className={`rounded-full border px-4 py-1.5 text-xs font-mono uppercase tracking-widest transition-colors ${
                  sub === s
                    ? "border-gold bg-gold text-burgundy font-medium"
                    : "border-cream/25 text-cream/60 hover:border-gold/50 hover:text-cream"
                }`}
              >
                {t.menu.sub[s]}
              </button>
            ))}
          </div>
        )}

        <div className="mt-10 min-h-[320px]" data-testid="menu-items-container">
          {loading && (
            <div className="space-y-5">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse h-16 rounded-lg bg-cream/5" />
              ))}
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <Wine className="w-8 h-8 text-gold/60" />
              <p className="text-cream/70">{t.menu.error}</p>
              <button
                onClick={load}
                data-testid="menu-retry-button"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-burgundy transition-colors"
              >
                <RotateCcw className="w-4 h-4" /> {t.menu.retry}
              </button>
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <Wine className="w-8 h-8 text-gold/60" />
              <p className="font-serif italic text-xl text-cream">{t.menu.empty}</p>
              <p className="text-sm text-cream/60">{t.menu.emptyHint}</p>
            </div>
          )}

          <AnimatePresence mode="popLayout">
            {!loading &&
              !error &&
              filtered.map((item) => (
                <motion.article
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="flex items-start justify-between gap-6 py-5 border-b border-cream/10 group"
                  data-testid={`menu-item-${item.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <h3 className="font-serif text-xl sm:text-2xl text-cream group-hover:text-gold transition-colors">
                        {item.name}
                      </h3>
                      {item.region && (
                        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-gold/70">{item.region}</span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm text-cream/55 leading-relaxed max-w-xl">
                      {isEn ? item.desc_en : item.desc_it}
                    </p>
                    {(item.tags_it || item.tags_en) && (
                      <div className="mt-2.5 flex gap-2">
                        {(isEn ? item.tags_en : item.tags_it)?.map((tag) => (
                          <span key={tag} className="rounded-full border border-gold/30 text-gold/80 text-[11px] px-3 py-0.5">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {priceCell(item)}
                </motion.article>
              ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
